import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavigationMenuContent } from "@/components/ui/navigation-menu";
import { contentfulVideoService } from '@/services/contentfulVideoService';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
interface VideosMenuProps {
  menuBg: string;
  menuHeaderText: string;
  menuItemText: string;
  menuText: string;
  hoverBg: string;
  borderColor: string;
  dividerColor: string;
  viewAllText: string;
}
const VideosMenu: React.FC<VideosMenuProps> = ({
  menuBg,
  menuHeaderText,
  menuItemText,
  menuText,
  hoverBg,
  borderColor,
  dividerColor,
  viewAllText
}) => {
  const navigate = useNavigate();

  // Fetch video categories
  const {
    data: videoCategories = [],
    isLoading
  } = useQuery({
    queryKey: ['videoCategories'],
    queryFn: contentfulVideoService.getVideoCategories,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // Get featured videos
  const {
    data: featuredVideos = []
  } = useQuery({
    queryKey: ['featuredVideos'],
    queryFn: contentfulVideoService.getFeaturedVideos,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // Get popular series videos
  const {
    data: joannaVideo
  } = useQuery({
    queryKey: ['video', 'joanna-delia-collector-house-film'],
    queryFn: () => contentfulVideoService.getVideoBySlug('joanna-delia-collector-house-film'),
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  const {
    data: ferrariVideo
  } = useQuery({
    queryKey: ['video', 'the-one-of-two-ferrari-daytona-365-gtb-4-in-verde-scuro'],
    queryFn: () => contentfulVideoService.getVideoBySlug('the-one-of-two-ferrari-daytona-365-gtb-4-in-verde-scuro'),
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  const {
    data: kissVideo
  } = useQuery({
    queryKey: ['video', 'inside-a-collectors-amazing-kiss-guitar-collection'],
    queryFn: () => contentfulVideoService.getVideoBySlug('inside-a-collectors-amazing-kiss-guitar-collection'),
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  const {
    data: collectionsVideo,
    isLoading: collectionsLoading,
    error: collectionsError
  } = useQuery({
    queryKey: ['video', 'collections'],
    queryFn: async () => {
      console.log('Fetching collections video...');
      const videos = await contentfulVideoService.getVideosByCategory('collections');
      console.log('Collections videos found:', videos);
      return videos[0] || null;
    },
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  console.log('Collections video:', collectionsVideo);
  console.log('Collections loading:', collectionsLoading);
  console.log('Collections error:', collectionsError);

  const featuredVideo = featuredVideos[0] || null;
  return <NavigationMenuContent className={`w-screen left-0 right-0 ${menuBg} shadow-[0_8px_16px_-4px_rgba(0,0,0,0.1)] border-b border-gray-200/10`}>
      <div className="container mx-auto grid gap-6 p-6 lg:grid-cols-[280px_1fr_1fr_280px]">
        {/* Left Column - Categories */}
        <div className="space-y-4">
          <h3 className={`text-sm font-medium uppercase tracking-wider ${menuHeaderText} font-chroma`}>WATCH VIDEOS</h3>
          <div className="space-y-2">
            {videoCategories.slice(0, 8).map((category, index) => {
              // Insert "All Videos" after Architecture
              const shouldInsertAllVideos = category.name === 'Architecture';
              return (
                <React.Fragment key={category.id}>
                  <button onClick={() => navigate(`/videos?category=${category.name.toLowerCase()}`)} className="group flex justify-between items-center w-full text-left py-0.5 text-foreground hover:text-foreground">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium group-hover:underline transition-all font-chroma">
                        {category.name}
                      </span>
                    </div>
                    <svg className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {shouldInsertAllVideos && (
                    <button onClick={() => navigate('/videos')} className="group flex justify-between items-center w-full text-left py-0.5 text-foreground hover:text-foreground">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium group-hover:underline transition-all font-chroma">
                          All Videos
                        </span>
                      </div>
                      <svg className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className="pt-4">
            <div className="flex flex-col gap-2 mt-3 pt-3">
              <div className="flex items-center gap-2">
                <a href="https://youtube.com/everhouseco" target="_blank" rel="noopener noreferrer" className={`text-xs ${menuText} opacity-70 hover:opacity-100 transition-opacity`}>Watch full episodes on</a>
                <a href="https://youtube.com/everhouseco" target="_blank" rel="noopener noreferrer" className={`${menuText} hover:opacity-70 transition-opacity`}>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a2.999 2.999 0 0 0-2.124-2.124C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.374.517A2.999 2.999 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a2.999 2.999 0 0 0 2.124 2.124C4.495 20.455 12 20.455 12 20.455s7.505 0 9.374-.517a2.999 2.999 0 0 0 2.124-2.124C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Middle Column - Latest Videos */}
        <div className="space-y-4">
          <h3 className={`text-sm font-medium uppercase tracking-wider ${menuHeaderText} font-chroma`}>LATEST VIDEOS</h3>
          <div className="space-y-4">
            {featuredVideos.slice(0, 3).map(video => <button key={video.id} onClick={() => navigate(`/videos/${video.slug || video.id}`)} className="group flex gap-4 text-left w-full">
                <div className="relative aspect-[16/9] w-32 flex-shrink-0 overflow-hidden">
                  <img src={video.thumbnailUrl} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <img src="/lovable-uploads/575a2bb1-d107-4904-aee1-b9abecc713b6.png" alt="Play" className="w-6 h-6" />
                  </div>
                </div>
                <div className="flex-1 space-y-1">
                  <h4 className={`text-sm font-medium ${menuItemText} group-hover:underline transition-all line-clamp-2 font-chroma`}>
                    {video.title}
                  </h4>
                </div>
              </button>)}
          </div>
        </div>
        
        {/* Third Column - Popular Series */}
        <div className="space-y-4">
          <h3 className={`text-sm font-medium uppercase tracking-wider ${menuHeaderText} font-chroma`}>POPULAR SERIES</h3>
          <div className="space-y-4">
            {joannaVideo && (
              <button onClick={() => navigate(`/videos/${joannaVideo.slug || joannaVideo.id}`)} className="group flex gap-4 text-left w-full">
                <div className="relative aspect-[16/9] w-32 flex-shrink-0 overflow-hidden">
                  <img src={joannaVideo.thumbnailUrl} alt={joannaVideo.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <img src="/lovable-uploads/575a2bb1-d107-4904-aee1-b9abecc713b6.png" alt="Play" className="w-6 h-6" />
                  </div>
                </div>
                <div className="flex-1 space-y-1">
                  <h4 className={`text-sm font-medium ${menuItemText} group-hover:underline transition-all line-clamp-2 font-chroma`}>
                    {joannaVideo.title}
                  </h4>
                  <p className={`text-xs ${menuText} opacity-70`}>{joannaVideo.description}</p>
                </div>
              </button>
            )}
            
            {ferrariVideo && (
              <button onClick={() => navigate(`/videos/${ferrariVideo.slug || ferrariVideo.id}`)} className="group flex gap-4 text-left w-full">
                <div className="relative aspect-[16/9] w-32 flex-shrink-0 overflow-hidden">
                  <img src={ferrariVideo.thumbnailUrl} alt={ferrariVideo.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <img src="/lovable-uploads/575a2bb1-d107-4904-aee1-b9abecc713b6.png" alt="Play" className="w-6 h-6" />
                  </div>
                </div>
                <div className="flex-1 space-y-1">
                  <h4 className={`text-sm font-medium ${menuItemText} group-hover:underline transition-all line-clamp-2 font-chroma`}>
                    {ferrariVideo.title}
                  </h4>
                  <p className={`text-xs ${menuText} opacity-70`}>{ferrariVideo.description}</p>
                </div>
              </button>
            )}
            
            {kissVideo && (
              <button onClick={() => navigate(`/videos/${kissVideo.slug || kissVideo.id}`)} className="group flex gap-4 text-left w-full">
                <div className="relative aspect-[16/9] w-32 flex-shrink-0 overflow-hidden">
                  <img src={kissVideo.thumbnailUrl} alt={kissVideo.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <img src="/lovable-uploads/575a2bb1-d107-4904-aee1-b9abecc713b6.png" alt="Play" className="w-6 h-6" />
                  </div>
                </div>
                <div className="flex-1 space-y-1">
                  <h4 className={`text-sm font-medium ${menuItemText} group-hover:underline transition-all line-clamp-2 font-chroma`}>
                    {kissVideo.title}
                  </h4>
                  <p className={`text-xs ${menuText} opacity-70`}>{kissVideo.description}</p>
                </div>
              </button>
            )}
            
            {collectionsVideo && (
              <button onClick={() => navigate(`/videos/${collectionsVideo.slug || collectionsVideo.id}`)} className="group flex gap-4 text-left w-full">
                <div className="relative aspect-[16/9] w-32 flex-shrink-0 overflow-hidden">
                  <img src={collectionsVideo.thumbnailUrl} alt={collectionsVideo.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <img src="/lovable-uploads/575a2bb1-d107-4904-aee1-b9abecc713b6.png" alt="Play" className="w-6 h-6" />
                  </div>
                </div>
                <div className="flex-1 space-y-1">
                  <h4 className={`text-sm font-medium ${menuItemText} group-hover:underline transition-all line-clamp-2 font-chroma`}>
                    {collectionsVideo.title}
                  </h4>
                  <p className={`text-xs ${menuText} opacity-70`}>{collectionsVideo.description}</p>
                </div>
              </button>
            )}
          </div>
        </div>
        
        {/* Fourth Column - Featured Content */}
        <div className="space-y-4">
          <h3 className={`text-sm font-medium uppercase tracking-wider ${menuHeaderText} font-chroma`}>FEATURED</h3>
          
          {/* Main Featured Video - Goktug Sarioz's Saddle Peak House */}
          <div className="group space-y-3">
            <button onClick={() => navigate('/videos/goktug-sarioz-creative-house-film')} className="aspect-[16/9] w-full overflow-hidden relative">
              <img src="https://i.vimeocdn.com/video/2002683130-e30c0d9b7e6ee1fbe12afb396289465354b3fc547eba9fef206be353d5b72d5b-d?mw=1920&q=85" alt="Goktug Sarioz's Saddle Peak House" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <img src="/lovable-uploads/575a2bb1-d107-4904-aee1-b9abecc713b6.png" alt="Play" className="w-8 h-8" />
              </div>
            </button>
            <div className="space-y-1">
              <h4 className={`text-sm font-medium ${menuItemText} group-hover:underline transition-all line-clamp-2 font-chroma`}>
                Goktug Sarioz's Saddle Peak House
              </h4>
              <p className={`text-xs ${menuText} opacity-70 line-clamp-2`}>
                Explore this stunning modern house in California, showcasing exceptional architectural design and creative vision.
              </p>
            </div>
          </div>
          
        </div>
      </div>
    </NavigationMenuContent>;
};
export default VideosMenu;